import { Injectable, inject } from '@angular/core';
import { ApiService } from '../../../core/services/api.service';
import {
  AvailabilityCheckResponse,
  UserMenuPrivilege,
  Vendor,
  VendorSavePayload,
  VendorUser,
  VendorUserCreatePayload,
} from '../../../core/models/domain.models';

@Injectable({ providedIn: 'root' })
export class VendorsService {
  private readonly api = inject(ApiService);

  list() {
    return this.api.get<Vendor[]>('/api/vendors');
  }

  get(id: string) {
    return this.api.get<Vendor>(`/api/vendors/${id}`);
  }

  create(payload: Partial<Vendor>) {
    return this.api.post<Vendor>('/api/vendors', payload);
  }

  update(id: string, payload: Partial<Vendor>) {
    return this.api.patch<Vendor>(`/api/vendors/${id}`, payload);
  }

  save(payload: VendorSavePayload, vendorId?: string) {
    if (vendorId) {
      return this.api.put<Vendor>(`/api/vendors/${vendorId}/save`, payload);
    }
    return this.api.post<Vendor>('/api/vendors/save', payload);
  }

  validateNoarId(noarId: string, excludeVendorId?: string) {
    const params: Record<string, string> = { noar_id: noarId };
    if (excludeVendorId) params['vendor_id'] = excludeVendorId;
    return this.api.get<AvailabilityCheckResponse>('/api/vendors/validate-noar', params);
  }

  validateVendorUsername(username: string, vendorId?: string, excludeUserId?: string) {
    const params: Record<string, string> = { username };
    if (vendorId) params['vendor_id'] = vendorId;
    if (excludeUserId) params['user_id'] = excludeUserId;
    return this.api.get<AvailabilityCheckResponse>('/api/vendors/validate-username', params);
  }

  listUsers(vendorId: string) {
    return this.api.get<VendorUser[]>(`/api/vendors/${vendorId}/users`);
  }

  syncUsers(vendorId: string, users: VendorUserCreatePayload[]) {
    return this.api.post<VendorUser[]>(`/api/vendors/${vendorId}/users/sync`, { users });
  }

  listUserPrivileges(vendorId: string, userId: string) {
    return this.api.get<UserMenuPrivilege[]>(`/api/vendors/${vendorId}/users/${userId}/privileges`);
  }

  saveUserPrivileges(
    vendorId: string,
    userId: string,
    payload: { items: Array<{ menu_id: string; can_read: boolean; can_write: boolean }> },
  ) {
    return this.api.put<UserMenuPrivilege[]>(`/api/vendors/${vendorId}/users/${userId}/privileges`, payload);
  }

  delete(id: string) {
    return this.api.delete<{ message: string }>(`/api/vendors/${id}`);
  }
}
